import { CSSProperties } from 'react';
import { tokens } from '../../tokens';

export interface TxFilters {
  buy: boolean;
  sell: boolean;
  core: boolean;
  tp: boolean;
  sl: boolean;
  /** CPC balance-distribution syncs (subscription only). */
  dist: boolean;
  /** Balance deposits/withdrawals/corrections (subscription only). */
  balance: boolean;
  /** Earn interest credits (subscription only). */
  interest: boolean;
  /** 'YYYY-MM-DD' or '' for no bound. */
  dateStart: string;
  dateEnd: string;
}

export const DEFAULT_TX_FILTERS: TxFilters = {
  buy: true,
  sell: true,
  core: true,
  tp: true,
  sl: true,
  dist: true,
  balance: true,
  // Interest is credited daily, so leaving it on would bury every real trade under a year of
  // sub-cent rows. It is opt-in; the cumulative figure is reported on the page regardless.
  interest: false,
  dateStart: '',
  dateEnd: '',
};

interface ChipProps {
  label: string;
  color: string;
  on: boolean;
  onClick: () => void;
}

const Chip = ({ label, color, on, onClick }: ChipProps) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 7,
      padding: '6px 12px',
      background: on ? 'rgba(255,255,255,0.05)' : 'transparent',
      border: `1px solid ${on ? `${color}99` : 'rgba(255,255,255,0.14)'}`,
      borderRadius: 3,
      color: on ? tokens.textPrimary : tokens.textMuted,
      fontFamily: tokens.fontMono,
      fontSize: 10,
      fontWeight: 600,
      letterSpacing: '0.12em',
      cursor: 'pointer',
    }}
  >
    <span
      style={{
        width: 6,
        height: 6,
        borderRadius: '50%',
        background: on ? color : tokens.textFaint,
      }}
    />
    {label}
  </button>
);

const labelStyle: CSSProperties = {
  fontSize: 10,
  color: tokens.textMuted,
  fontFamily: tokens.fontMono,
  letterSpacing: '0.15em',
  fontWeight: 700,
};

const dateInputStyle: CSSProperties = {
  background: tokens.backgroundRaised,
  border: '1px solid rgba(255,255,255,0.14)',
  borderRadius: 3,
  color: tokens.textSecondary,
  fontFamily: tokens.fontMono,
  fontSize: 11,
  padding: '5px 8px',
  colorScheme: 'dark',
};

interface Props {
  filters: TxFilters;
  onChange: (next: TxFilters) => void;
  /** Hide the TAKE PROFIT chip when no take-profit transactions exist. */
  hasTakeProfit?: boolean;
  /** Hide the STOP LOSS chip when no stop-loss transactions exist. */
  hasStopLoss?: boolean;
  /** Hide the DISTRIBUTION chip when no distribution syncs exist. */
  hasDistribution?: boolean;
  /** Hide the BALANCE chip when no deposits/withdrawals/corrections exist. */
  hasBalanceChange?: boolean;
  /** Hide the INTEREST chip when no earn interest has been credited. */
  hasInterest?: boolean;
}

const FilterRow = ({
  filters,
  onChange,
  hasTakeProfit = true,
  hasStopLoss = true,
  hasDistribution = false,
  hasBalanceChange = false,
  hasInterest = false,
}: Props) => {
  const update = (patch: Partial<TxFilters>) =>
    onChange({ ...filters, ...patch });

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 18,
        padding: '0px 0 32px',
        maxWidth: 1400,
        margin: '0 auto 4px',
      }}
    >
      <span style={labelStyle}>FILTER</span>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Chip
          label="CORE"
          color={tokens.green}
          on={filters.core}
          onClick={() => update({ core: !filters.core })}
        />
        {hasTakeProfit && (
          <Chip
            label="TAKE PROFIT"
            color={tokens.yellow}
            on={filters.tp}
            onClick={() => update({ tp: !filters.tp })}
          />
        )}
        {hasStopLoss && (
          <Chip
            label="STOP LOSS"
            color={tokens.blue}
            on={filters.sl}
            onClick={() => update({ sl: !filters.sl })}
          />
        )}
        {hasDistribution && (
          <Chip
            label="DISTRIBUTION"
            color={tokens.amber}
            on={filters.dist}
            onClick={() => update({ dist: !filters.dist })}
          />
        )}
        {hasBalanceChange && (
          <Chip
            label="BALANCE"
            color={tokens.cyan}
            on={filters.balance}
            onClick={() => update({ balance: !filters.balance })}
          />
        )}
        {hasInterest && (
          <Chip
            label="INTEREST"
            color={tokens.cyan}
            on={filters.interest}
            onClick={() => update({ interest: !filters.interest })}
          />
        )}
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Chip
          label="BUY"
          color={tokens.green}
          on={filters.buy}
          onClick={() => update({ buy: !filters.buy })}
        />
        <Chip
          label="SELL"
          color={tokens.red}
          on={filters.sell}
          onClick={() => update({ sell: !filters.sell })}
        />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={labelStyle}>FROM</span>
        <input
          type="date"
          value={filters.dateStart}
          max={filters.dateEnd || undefined}
          onChange={(event) => update({ dateStart: event.target.value })}
          style={dateInputStyle}
        />
        <span style={labelStyle}>TO</span>
        <input
          type="date"
          value={filters.dateEnd}
          min={filters.dateStart || undefined}
          onChange={(event) => update({ dateEnd: event.target.value })}
          style={dateInputStyle}
        />
      </div>
    </div>
  );
};

export default FilterRow;
